import React, { useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    LifeBuoy,
    Clock,
    CheckCircle2,
    AlertCircle,
    User,
    Calendar,
} from "lucide-react";

const TICKETS = [
    {
        id: "TCK-1042",
        subject: "CNC machine not responding to job start",
        raisedBy: "Ravi Teja",
        centre: "Vizag",
        category: "Machine Issue",
        priority: "High",
        status: "Open",
        date: "18 Jul 2026",
        assignedTo: "Priya Nair",
    },
    {
        id: "TCK-1041",
        subject: "Unable to upload design files to project",
        raisedBy: "Lakshmi Devi",
        centre: "Vijayawada",
        category: "Software",
        priority: "Medium",
        status: "In Progress",
        date: "18 Jul 2026",
        assignedTo: "Arjun Kumar",
    },
    {
        id: "TCK-1039",
        subject: "Booking slot double allocated for 3D printer",
        raisedBy: "Sandeep Reddy",
        centre: "Tirupati",
        category: "Booking",
        priority: "High",
        status: "Open",
        date: "17 Jul 2026",
        assignedTo: "Unassigned",
    },
    {
        id: "TCK-1036",
        subject: "Invoice amount mismatch for last order",
        raisedBy: "Meera Iyer",
        centre: "Kurnool",
        category: "Billing",
        priority: "Low",
        status: "Resolved",
        date: "16 Jul 2026",
        assignedTo: "Priya Nair",
    },
    {
        id: "TCK-1033",
        subject: "Laser cutter emitting unusual noise during operation",
        raisedBy: "Kiran Babu",
        centre: "Guntur",
        category: "Machine Issue",
        priority: "High",
        status: "In Progress",
        date: "15 Jul 2026",
        assignedTo: "Arjun Kumar",
    },
    {
        id: "TCK-1030",
        subject: "Request to reset operator login credentials",
        raisedBy: "Divya Sri",
        centre: "Nellore",
        category: "Access",
        priority: "Medium",
        status: "Resolved",
        date: "14 Jul 2026",
        assignedTo: "Priya Nair",
    },
];

const STATUS_STYLES = {
    Open: "bg-red-100 text-red-700",
    "In Progress": "bg-amber-100 text-amber-700",
    Resolved: "bg-green-100 text-green-700",
};

const PRIORITY_STYLES = {
    High: "bg-red-50 text-red-600 border border-red-200",
    Medium: "bg-amber-50 text-amber-600 border border-amber-200",
    Low: "bg-slate-50 text-slate-600 border border-slate-200",
};

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function SupportDashboard() {
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Status");
    const [priorityFilter, setPriorityFilter] = useState("All Priority");

    const statuses = useMemo(
        () => ["All Status", ...new Set(TICKETS.map((t) => t.status))],
        []
    );
    const priorities = useMemo(
        () => ["All Priority", ...new Set(TICKETS.map((t) => t.priority))],
        []
    );

    const counts = useMemo(() => {
        const open = TICKETS.filter((t) => t.status === "Open").length;
        const inProgress = TICKETS.filter((t) => t.status === "In Progress").length;
        const resolved = TICKETS.filter((t) => t.status === "Resolved").length;
        return { total: TICKETS.length, open, inProgress, resolved };
    }, []);

    const filtered = useMemo(() => {
        return TICKETS.filter((t) => {
            const matchesQuery =
                query.trim() === "" ||
                [t.id, t.subject, t.raisedBy, t.centre].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesStatus = statusFilter === "All Status" || t.status === statusFilter;
            const matchesPriority = priorityFilter === "All Priority" || t.priority === priorityFilter;
            return matchesQuery && matchesStatus && matchesPriority;
        });
    }, [query, statusFilter, priorityFilter]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Support tickets</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track and resolve tickets raised across all centres
                    </p>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={LifeBuoy} label="Total tickets" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={AlertCircle} label="Open" value={counts.open} tone="bg-red-50 text-red-500" />
                    <StatCard icon={Clock} label="In progress" value={counts.inProgress} tone="bg-amber-50 text-amber-500" />
                    <StatCard icon={CheckCircle2} label="Resolved" value={counts.resolved} tone="bg-green-50 text-green-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Raised tickets</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {filtered.length} of {TICKETS.length} tickets need resolution
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search ticket, name, centre..."
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {statuses.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="relative">
                                <select
                                    value={priorityFilter}
                                    onChange={(e) => setPriorityFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {priorities.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1000px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-[26%] px-6 py-3 font-medium">Ticket</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Raised by</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Category</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Priority</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Status</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Date</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Assigned to</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((t, idx) => (
                                    <tr
                                        key={t.id}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-gray-900">{t.subject}</p>
                                            <p className="mt-0.5 text-xs text-gray-400">
                                                {t.id} · {t.centre}
                                            </p>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div className="flex items-center gap-2">
                                                <User className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{t.raisedBy}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{t.category}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${PRIORITY_STYLES[t.priority]}`}>
                                                {t.priority}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[t.status]}`}>
                                                {t.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                                                {t.date}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {t.assignedTo === "Unassigned" ? (
                                                <span className="text-gray-400">Unassigned</span>
                                            ) : (
                                                t.assignedTo
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No tickets match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}